/**
 * Markdown editor for one document section, focusing purely on Rich mode (TipTap WYSIWYG).
 *
 * Mounts {@link TipTapEditor}, a ProseMirror WYSIWYG. Its markdown contract
 * lives in `tiptapMarkdown.ts` and is guarded against the real exemplar corpus
 * by `markdownRoundTrip.test.ts`, so it does not corrupt the word-level version
 * diff / `.md` export / sync-back the way an HTML intermediate would.
 *
 * Central undo/redo, typography formatting, and palette controls are provided
 * by the unified `DocumentRibbon`, keeping the section editing canvas clean
 * and unencumbered without extra header chrome.
 */
import React, { useEffect, useRef } from "react";
import type {
  CommentAnchor,
  DocumentComment,
  SourceCitation,
} from "../../types";
import TipTapEditor, { type TipTapEditorHandle } from "./TipTapEditor";

/** Props for {@link MarkdownEditor}. */
interface MarkdownEditorProps {
  /** The section key being edited (used for labelling/test ids). */
  sectionKey: string;
  /** The current markdown value. */
  value: string;
  /** Whether editing is disabled (e.g. a locked structural edit). */
  disabled?: boolean;
  /** Called with the new markdown whenever the value changes. */
  onChange: (markdown: string) => void;
  /**
   * `{ source id: title }` so an inline `[[src:...]]` marker renders a human
   * source label instead of the neutral fallback. The raw id is never shown.
   */
  sourceTitles?: Record<string, string> | null;
  /**
   * Per-claim provenance for the body's citation markers. When a marker's ref
   * matches an entry, its popover gains the verbatim cited excerpt and a
   * working "Go to source"; absent entries degrade to the hover label.
   */
  citations?: SourceCitation[] | null;
  /** `{ card id: name }` so an inference marker can name its contributing cards. */
  cardTitles?: Record<string, string> | null;
  /** Comments to render as inline highlights in Rich mode (BLA-70). */
  comments?: DocumentComment[];
  /** The comment id to render as active, or null. */
  activeCommentId?: string | null;
  /** The comment id to render as hovered (BLA-318), or null. */
  hoveredCommentId?: string | null;
  /** Called when a comment highlight is clicked. */
  onSelectComment?: (id: string) => void;
  /** Called when a comment highlight is hovered (or unhovered, with `null`). */
  onHoverComment?: (id: string | null) => void;
  /** Called when the provider comments on the current Rich-mode selection. */
  onCreateComment?: (anchor: CommentAnchor, clientY?: number) => void;
  /** Optional callback publishing the rich editor's active handle to parent (for ribbon synchronization). */
  onActiveHandle?: (handle: TipTapEditorHandle | null) => void;
  /** Optional callback notifying parent when this section's editor gains focus. */
  onFocus?: () => void;
  /** Optional callback notifying parent when this section's editor loses focus. */
  onBlur?: () => void;
}

/**
 * Render the section editor backed by TipTap WYSIWYG.
 *
 * @param props - Section properties, value, comments, and handlers.
 * @returns The editor element.
 */
export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  sectionKey,
  value,
  disabled = false,
  onChange,
  sourceTitles,
  citations,
  cardTitles,
  comments,
  activeCommentId,
  hoveredCommentId,
  onSelectComment,
  onHoverComment,
  onCreateComment,
  onActiveHandle,
  onFocus,
  onBlur,
}) => {
  const historyRef = useRef<TipTapEditorHandle | null>(null);
  const onActiveHandleRef = useRef(onActiveHandle);
  const onFocusRef = useRef(onFocus);
  useEffect(() => {
    onActiveHandleRef.current = onActiveHandle;
    onFocusRef.current = onFocus;
  }, [onActiveHandle, onFocus]);

  const handleSetHistory = React.useCallback(
    (handle: TipTapEditorHandle | null): void => {
      historyRef.current = handle;
      onActiveHandleRef.current?.(handle);
    },
    [],
  );

  const handleFocus = React.useCallback((): void => {
    if (historyRef.current) {
      onActiveHandleRef.current?.(historyRef.current);
    }
    onFocusRef.current?.();
  }, []);

  const handleBlur = React.useCallback((): void => {
    onBlur?.();
  }, [onBlur]);

  return (
    <div className="w-full">
      <TipTapEditor
        key={sectionKey}
        ref={handleSetHistory}
        sectionKey={sectionKey}
        value={value}
        disabled={disabled}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        sourceTitles={sourceTitles}
        citations={citations}
        cardTitles={cardTitles}
        comments={comments}
        activeCommentId={activeCommentId}
        hoveredCommentId={hoveredCommentId}
        onSelectComment={onSelectComment}
        onHoverComment={onHoverComment}
        onCreateComment={onCreateComment}
      />
    </div>
  );
};

export default MarkdownEditor;
